import React, { type ReactNode } from 'react';

import { GroupDropIndicator } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/group-drop-indicator';

import { createElementRegistry, ElementRegistryProvider } from '../../controllers/element-registry';
import { createNestedItemData, type OrderPreference, type RawItemData } from '../../utils/dnd-data';
import type { AnalyticsEventHandler } from '../../utils/use-item-drop';
import { useNestedItemDropTarget } from '../../utils/use-item-drop/nested-item-drop-target';

export interface NestedItemDropTargetProps {
	children: ReactNode;
	currentItems: RawItemData[];
	currentOrder: OrderPreference;
	entityAri: string;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	group: string;
	ownerAri: string;
	testId?: string;
}

export function NestedItemDropTarget({
	children,
	currentItems,
	currentOrder,
	entityAri,
	fireAnalyticsEvent,
	group,
	ownerAri,
	testId,
}: NestedItemDropTargetProps): React.JSX.Element {
	const mappedCurrentItems = currentItems.map((item) => {
		return createNestedItemData({
			menuItemId: item.id,
			entityAri,
			ownerAri,
			group: entityAri,
			label: item.label,
			isReorderable: true,
		});
	});

	const { ref, state } = useNestedItemDropTarget({
		entityAri,
		ownerAri,
		currentItems: mappedCurrentItems,
		currentOrder,
		fireAnalyticsEvent,
		group,
	});

	const elementRegistry = React.useMemo(() => createElementRegistry(), []);
	return (
		<GroupDropIndicator isActive={state === 'is-over'} ref={ref} data-testid={testId}>
			<ElementRegistryProvider registry={elementRegistry}>{children}</ElementRegistryProvider>
		</GroupDropIndicator>
	);
}
